import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  useTheme,
} from '@mui/material';
import { setLoading } from '../reducers/loadingReducer';
import { sendUserInvitation } from '../services/apiService';
import LoadingSpinner from './LoadingSpinner';
import useResponsive from '../hooks/useResponsive';

const AdminUserInvite = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loading);
  const [editMode, setEditMode] = useState(false);
  const [userLocal, setUser] = useState({
    firstname: '',
    lastname: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const { isMobileScreen } = useResponsive();

  const handleEditToggle = () => setEditMode(!editMode);

  const handleUserChange = (e) =>
    setUser({ ...userLocal, [e.target.name]: e.target.value });

  const validate = () => {
    let tempErrors = {};
    tempErrors.firstname = userLocal.firstname ? '' : 'First Name is required.';
    tempErrors.lastname = userLocal.lastname ? '' : 'Last Name is required.';
    tempErrors.email = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(userLocal.email)
      ? ''
      : 'Email is not valid.';
    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === '');
  };

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    if (validate()) {
      dispatch(setLoading(true));
      try {
        await sendUserInvitation(userLocal);
        dispatch(setLoading(false));
        setEditMode(false);
        setUser({ firstname: '', lastname: '', email: '' });
      } catch (error) {
        dispatch(setLoading(false));
        console.error('Failed to send invitation:', error);
      }
    }
  };

  return (
    <Box
      sx={{
        margin: 2,
        padding: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top,rgba(18, 18, 18, 0.8),rgba(29, 29, 29, 0.6))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.2), rgba(0,116,211,0.1))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSendInvitation}
        sx={{
          width: '100%',
          maxWidth: 600,
          padding: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <TextField
          fullWidth
          margin="normal"
          label="First Name"
          name="firstname"
          value={userLocal.firstname}
          onChange={handleUserChange}
          disabled={!editMode || loading}
          required
          variant="outlined"
          error={!!errors.firstname}
          helperText={errors.firstname}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Last Name"
          name="lastname"
          value={userLocal.lastname}
          onChange={handleUserChange}
          disabled={!editMode || loading}
          required
          variant="outlined"
          error={!!errors.lastname}
          helperText={errors.lastname}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Email Address"
          name="email"
          value={userLocal.email}
          onChange={handleUserChange}
          disabled={!editMode || loading}
          required
          variant="outlined"
          error={!!errors.email}
          helperText={errors.email}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 2,
            flexDirection: isMobileScreen ? 'column' : 'row',
            gap: 2,
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center">
              <LoadingSpinner />
            </Box>
          ) : editMode ? (
            <>
              <Button variant="contained" color="primary" type="submit">
                Send Invitation
              </Button>
              <Button onClick={handleEditToggle}>Cancel</Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleEditToggle}>
              Unlock Form
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminUserInvite;
